<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { Facade, DraggerType } from "@/drawing";
import type { HandwritingStatus } from "@/drawing/handwriting/types";
import { getHandwritingRecognizer } from "@/services/handwriting/TrocrRecognizer";
import HandwritingFeatureState from "@/stores/HandwritingFeatureState";
import HandwritingOnboardingModal from "./HandwritingOnboardingModal.vue";
import StylusTextModal from "./StylusTextModal.vue";
import {
  supportsStylusHandwriting,
  resolveStylusHandwritingSupport,
} from "@/utils/stylusHandwriting";
import { HANDWRITING_TOOL_ENABLED } from "@/services/handwriting/featureFlag";
import UserState from "@/stores/UserState";
import DraggerMenuState from "@/stores/DraggerMenuState";
import { Logger } from "@/logger";

/**
 * Les variantes "écriture" de l'outil texte : reconnaissance d'écriture
 * (draw2text) et clavier stylet. Vivent dans le panneau de l'outil texte,
 * sous le champ de saisie ; rien n'est rendu quand aucune n'est disponible.
 */

const handwritingStatus = ref<HandwritingStatus>("idle");

const isHandwritingActive = computed(() => {
  return DraggerMenuState.activeDragger.name === DraggerType.Handwriting;
});

const isStylusKeyboardActive = computed(() => {
  return DraggerMenuState.activeDragger.name === DraggerType.StylusKeyboard;
});

const isConverting = computed(() => handwritingStatus.value === "converting");
const isRecognitionError = computed(() => handwritingStatus.value === "error");
const canConvertNow = computed(
  () =>
    handwritingStatus.value === "inked" || handwritingStatus.value === "error",
);

const lastErrorDetail = computed(() =>
  isRecognitionError.value && Facade.isInitialized
    ? Facade.handwriting.lastError
    : null,
);

// Bind recognizer + status listener once the canvas is ready. The panel is
// mounted before Facade init, so bind lazily on first activation.
let unsubscribeStatus: (() => void) | null = null;

watch(
  isHandwritingActive,
  (active) => {
    if (!HANDWRITING_TOOL_ENABLED) return;
    if (!active || unsubscribeStatus || !Facade.isInitialized) return;
    Facade.handwriting.setRecognizer(getHandwritingRecognizer());
    unsubscribeStatus = Facade.handwriting.onStatusChange((status, detail) => {
      handwritingStatus.value = status;
      // Pas de toast dans l'outil dessin : la raison technique s'affiche déjà
      // dans le bouton (cf. `lastErrorDetail`), on la journalise en plus.
      if (status === "error" && detail) {
        Logger.error(`HandwritingToolButtons : ${detail}`);
      }
    });
    handwritingStatus.value = Facade.handwriting.status;
  },
  { immediate: true },
);

// Stylus-keyboard mode: the dragger tells us when the user has picked a
// position, we open the input where the device keyboard does the writing.
const isStylusModalVisible = ref(false);
let unsubscribePlacement: (() => void) | null = null;

watch(
  isStylusKeyboardActive,
  (active) => {
    if (!active || unsubscribePlacement || !Facade.isInitialized) return;
    unsubscribePlacement = Facade.stylusKeyboard.onPlacementRequest(() => {
      isStylusModalVisible.value = true;
    });
  },
  { immediate: true },
);

function onStylusTextValidated(text: string) {
  if (Facade.isInitialized) {
    Facade.stylusKeyboard.insertText(text);
  }
}

function onStylusTextCancelled() {
  if (Facade.isInitialized) {
    Facade.stylusKeyboard.cancelPlacement();
  }
}

// Offered to superadmins only for now (field trial), and only where the OS
// converts stylus handwriting (iPad, Samsung): everywhere else the mode
// would open an empty field and nothing more. The device answer starts
// synchronous and is refined once the browser reports its model — Chrome
// on Android keeps it out of the user agent.
const isStylusDevice = ref(supportsStylusHandwriting());
void resolveStylusHandwritingSupport().then((supported) => {
  isStylusDevice.value = supported;
});

const showStylusKeyboard = computed(
  () => UserState.isSuperAdmin() && isStylusDevice.value,
);

function activateStylusKeyboard() {
  if (isStylusKeyboardActive.value) return;
  DraggerMenuState.setActiveDragger({ name: DraggerType.StylusKeyboard });
}

onUnmounted(() => {
  unsubscribeStatus?.();
  unsubscribeStatus = null;
  unsubscribePlacement?.();
  unsubscribePlacement = null;
});

const isOnboardingVisible = ref(false);

const featureStatus = computed(() => HandwritingFeatureState.status);

const showDiscover = computed(
  () => HANDWRITING_TOOL_ENABLED && featureStatus.value === "new",
);
const showInstalled = computed(
  () => HANDWRITING_TOOL_ENABLED && featureStatus.value === "installed",
);
const hasAnyButton = computed(
  () => showDiscover.value || showInstalled.value || showStylusKeyboard.value,
);

function activateHandwriting() {
  if (isHandwritingActive.value) return;
  DraggerMenuState.setActiveDragger({ name: DraggerType.Handwriting });
}

function openOnboarding() {
  isOnboardingVisible.value = true;
}

// À la fin de l'installation, activer directement l'outil : l'utilisateur
// vient de lire l'explication, il peut essayer immédiatement.
function onFeatureInstalled() {
  activateHandwriting();
}

function convertNow() {
  if (Facade.isInitialized) {
    Facade.handwriting.convertNow();
  }
}
</script>

<template>
  <div v-if="hasAnyButton" class="d-flex flex-row align-items-stretch gap-2">
    <!-- Handwriting (draw2text): discovery button with "new" badge -->
    <button
      v-if="showDiscover"
      class="rounded handwriting-btn handwriting-discover-btn d-flex align-items-center gap-1 px-2"
      type="button"
      @click="openOnboarding"
    >
      <PhosphorIcon name="signature" :size="20" />
      <span>{{ $t("canvas:tools.handwriting.discoverLabel") }}</span>
      <span class="handwriting-new-badge">{{
        $t("canvas:tools.handwriting.newBadge")
      }}</span>
    </button>

    <!-- Handwriting (draw2text): compact tool button once installed -->
    <button
      v-else-if="showInstalled"
      class="rounded handwriting-btn handwriting-square-btn d-flex align-items-center justify-content-center"
      :class="
        isHandwritingActive
          ? 'tool-btn-active handwriting-btn-active'
          : 'tool-btn-inactive'
      "
      type="button"
      :title="$t('canvas:tools.handwriting.label')"
      @click="activateHandwriting"
    >
      <PhosphorIcon name="signature" :size="20" />
    </button>

    <!-- Stylus keyboard: tap the drawing, write with the device keyboard -->
    <button
      v-if="showStylusKeyboard"
      class="rounded handwriting-btn handwriting-square-btn d-flex align-items-center justify-content-center"
      :class="isStylusKeyboardActive ? 'tool-btn-active' : 'tool-btn-inactive'"
      type="button"
      :title="$t('canvas:tools.stylusKeyboard.label')"
      @click="activateStylusKeyboard"
    >
      <PhosphorIcon name="signature" :size="20" />
    </button>

    <!-- Convert-now / progress, only while writing -->
    <button
      v-if="isHandwritingActive && (canConvertNow || isConverting)"
      class="rounded convert-btn d-flex align-items-center justify-content-center px-2"
      :class="{ 'convert-btn-error': isRecognitionError }"
      type="button"
      :disabled="isConverting"
      :title="
        isRecognitionError
          ? lastErrorDetail || $t('canvas:tools.handwriting.error')
          : $t('canvas:tools.handwriting.convert')
      "
      @click="convertNow"
    >
      <span
        v-if="isConverting"
        class="spinner-border spinner-border-sm"
        role="status"
      />
      <PhosphorIcon
        v-else-if="isRecognitionError"
        name="arrow-clockwise"
        :size="18"
      />
      <PhosphorIcon v-else name="check" weight="bold" :size="18" />
    </button>

    <HandwritingOnboardingModal
      v-if="HANDWRITING_TOOL_ENABLED"
      v-model="isOnboardingVisible"
      @installed="onFeatureInstalled"
    />

    <StylusTextModal
      v-model="isStylusModalVisible"
      @validate="onStylusTextValidated"
      @cancel="onStylusTextCancelled"
    />
  </div>
</template>

<style lang="scss" scoped>
// Même scheme que les champs du panneau (.text-tool-field) : sombre, contour
// bleu quand l'outil est actif, atténué sinon.
.handwriting-btn {
  height: 40px;
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: white;
  font-size: 12px;
  white-space: nowrap;
}

.tool-btn-active {
  border-color: hsl(199, 89%, 55%);
  box-shadow: 0 0 0 1px hsla(199, 89%, 55%, 0.35);
}

.tool-btn-inactive {
  opacity: 0.55;
}

.handwriting-btn-active {
  color: hsl(199, 89%, 60%);
}

.handwriting-square-btn {
  width: 40px;
  padding: 0;
}

.handwriting-new-badge {
  background-color: var(--bs-warning);
  color: var(--bs-dark);
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 1px 7px;
}

.convert-btn {
  background-color: var(--bs-primary);
  color: white;
  height: 2.5rem;
  min-width: 2.5rem;
  border: none;

  &:disabled {
    opacity: 0.8;
  }
}

.convert-btn-error {
  background-color: var(--bs-danger);
}
</style>
